/**
 * Draft ⇄ `GeneticInfoDTO` conversion for the variant form (PTV2-1172).
 *
 * The draft is a flat `Record` keyed by DTO field name, holding UI-friendly
 * values (strings, string arrays, an ISO date, a plain lab label). The field
 * config in `variantFields.ts` renders it generically.
 *
 * `draftToDto` spreads the original DTO first, so fields the form doesn't edit
 * (`orderRefs`, `lock_version`, `id`, …) survive the full-replace PUT. Empty
 * scalars are omitted (the backend reads a missing key as unset), non-empty
 * ones overwrite; the `"na"` sentinel is normalised to empty on the way in.
 */

import type { GeneticInfoDto, LiveVariantPartialDate } from '@/features/patient-record/api';

import { REFERENCE_GENOME_DEFAULT } from '../../data/geneticFieldOptions';

export type VariantDraft = Record<string, string | readonly string[]>;

/** Scalar DTO fields the form edits directly as text/select values. */
const SCALAR_FIELDS = [
  'geneId',
  'geneSymbol',
  'variantType',
  'referenceGenome',
  'chromosome',
  'start',
  'end',
  'transcript',
  'protein',
  'cdna',
  'interpretation',
  'status',
  'refAllele',
  'altAllele',
  'zygositySnv',
  'cnvType',
  'cnvClassification',
  'cnvStructuralImpact',
  'cnvFunctionalImpact',
  'cytoband',
  'mtdna',
  'zygosityMtdna',
  'structuralType',
  'chromosomeBreakpoint',
  'positionBreakpoint',
  'fusion',
  'repeatUnit',
  'dbsnp',
  'inheritance',
  'sangerValidation',
  'segregation',
  'comments',
] as const;

/** Array-valued DTO fields (multi-selects + the free-form breakpoint list). */
const ARRAY_FIELDS = ['effects', 'strategies', 'evidences', 'breakpoints'] as const;

/** Numeric DTO fields and how to parse them. */
const NUMBER_FIELDS: Readonly<Record<string, 'int' | 'float'>> = {
  copyNumber: 'int',
  repeatCount: 'int',
  heteroplasmyPercent: 'float',
};

/** Pseudo-keys for fields with a shaped wire representation. */
export const DATE_FIELD = 'dateObserved';
export const LAB_FIELD = 'labName';

function str(v: string | undefined | null): string {
  return v === undefined || v === null || v === 'na' ? '' : v;
}

/** `{year, month, day}` → ISO `YYYY-MM-DD` (or the populated prefix). */
function partialDateToIso(d: LiveVariantPartialDate | null | undefined): string {
  if (d === null || d === undefined || typeof d.year !== 'number' || d.year === 0) {
    return '';
  }
  const parts = [String(d.year).padStart(4, '0')];
  if (typeof d.month === 'number' && d.month > 0) {
    parts.push(String(d.month).padStart(2, '0'));
    if (typeof d.day === 'number' && d.day > 0) {
      parts.push(String(d.day).padStart(2, '0'));
    }
  }
  return parts.join('-');
}

/** ISO `YYYY-MM-DD` (or partial) → `{year, month, day}` triple (0 for missing). */
function isoToPartialDate(iso: string): LiveVariantPartialDate | null {
  if (iso.length === 0) return null;
  const [y, m, d] = iso.split('-');
  const year = parseInt(y ?? '', 10);
  if (Number.isNaN(year)) return null;
  const month = m !== undefined ? parseInt(m, 10) : NaN;
  const day = d !== undefined ? parseInt(d, 10) : NaN;
  return {
    year,
    month: Number.isNaN(month) ? 0 : month,
    day: Number.isNaN(day) ? 0 : day,
  };
}

/** A blank draft for the create flow (genome build defaulted per the spec). */
export function emptyDraft(): VariantDraft {
  const draft: Record<string, string | readonly string[]> = {};
  for (const key of SCALAR_FIELDS) draft[key] = '';
  for (const key of Object.keys(NUMBER_FIELDS)) draft[key] = '';
  for (const key of ARRAY_FIELDS) draft[key] = [];
  draft[DATE_FIELD] = '';
  draft[LAB_FIELD] = '';
  draft.referenceGenome = REFERENCE_GENOME_DEFAULT;
  return draft;
}

/** Hydrate a draft from an existing DTO for the edit flow. */
export function dtoToDraft(dto: GeneticInfoDto): VariantDraft {
  const r = dto as unknown as Record<string, unknown>;
  const draft: Record<string, string | readonly string[]> = {};
  for (const key of SCALAR_FIELDS) draft[key] = str(r[key] as string | undefined);
  for (const key of Object.keys(NUMBER_FIELDS)) {
    const v = r[key];
    draft[key] = typeof v === 'number' ? String(v) : '';
  }
  for (const key of ARRAY_FIELDS) {
    const v = r[key];
    draft[key] = Array.isArray(v)
      ? v.filter((x): x is string => typeof x === 'string' && x !== 'na')
      : [];
  }
  draft[DATE_FIELD] = partialDateToIso(dto.dateObserved);
  draft[LAB_FIELD] =
    dto.source?.type === 'lab' ? str(dto.source.label) : '';
  return draft;
}

/** Build the full DTO to POST/PUT: original fields preserved, edits applied. */
export function draftToDto(draft: VariantDraft, base?: GeneticInfoDto): GeneticInfoDto {
  // Spread the original DTO first so fields the form doesn't edit survive the
  // full-replace PUT. Empty values are set to `undefined` (dropped by
  // JSON.stringify on the wire → read by the backend as unset/cleared).
  const dto: Record<string, unknown> = { ...(base ?? {}) };

  for (const key of SCALAR_FIELDS) {
    const v = (draft[key] as string | undefined)?.trim() ?? '';
    dto[key] = v.length > 0 ? v : undefined;
  }

  for (const [key, mode] of Object.entries(NUMBER_FIELDS)) {
    const raw = (draft[key] as string | undefined)?.trim() ?? '';
    const n = mode === 'int' ? parseInt(raw, 10) : parseFloat(raw);
    dto[key] = raw.length > 0 && !Number.isNaN(n) ? n : undefined;
  }

  for (const key of ARRAY_FIELDS) {
    const arr = (draft[key] as readonly string[] | undefined) ?? [];
    const clean = arr.map((x) => x.trim()).filter((x) => x.length > 0 && x !== 'na');
    dto[key] = clean.length > 0 ? clean : undefined;
  }

  dto.dateObserved = isoToPartialDate((draft[DATE_FIELD] as string | undefined) ?? '') ?? undefined;

  const lab = (draft[LAB_FIELD] as string | undefined)?.trim() ?? '';
  dto.source = lab.length > 0 ? { type: 'lab', label: lab } : undefined;

  return dto;
}
